import { BaseComponent } from '../base-component.js';
import { api } from '../../client/api.js';
import { session } from '../../client/session.js';
import { toast } from '../../client/toast.js';
import { escapeHtml, escapeAttribute, resolveImageUrl, stripHtml } from '../../shared/utils.js';
import type { PageConfig, FeaturedBlockData, LogoShape } from '../../shared/types.js';
import componentStyles from './page-editor.css?inline';

const CHAR_LIMIT = 1200;

export class PageEditor extends BaseComponent {
  private config: PageConfig | null = null;
  private loading = true;
  private saving = false;
  private featuredBlock: FeaturedBlockData | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadConfig();
  }

  protected styles(): string {
    return componentStyles;
  }

  protected template(): string {
    if (this.loading) {
      return '<div class="loading">Loading page settings...</div>';
    }

    const c = this.config!;
    const safeTitle = escapeAttribute(c.title || '');
    const safeBio = escapeHtml(c.bio || '');
    const rawLogoUrl = escapeAttribute(c.logoUrl || '');
    const rawBackgroundUrl = escapeAttribute(c.backgroundUrl || '');
    const rawFeaturedImageUrl = escapeAttribute(this.featuredBlock?.imageUrl || '');
    const safeLogoUrl = resolveImageUrl(c.logoUrl);
    const safeBackgroundUrl = resolveImageUrl(c.backgroundUrl);
    const safeFeaturedImageUrl = resolveImageUrl(this.featuredBlock?.imageUrl);
    const safeAccentColor = escapeAttribute(c.accentColor || '#3b82f6');
    const safeBgColor = escapeAttribute(c.backgroundColor || '#ffffff');
    const selectedShape: LogoShape = c.logoShape || 'circle';

    return `
      <ag-panel>
        <ag-panel-header>
          <h2>Page Design</h2>
          <p class="description">Customize the appearance and content of your personal landing page.</p>
        </ag-panel-header>
        <ag-panel-content>
          <form id="pageForm">
            <div class="section">
              <div class="section-title">Content</div>
              
              <ag-form-group>
                <label for="title">Page Title</label>
                <input class="form-control" type="text" id="title" name="title" value="${safeTitle}" placeholder="Your name or brand">
              </ag-form-group>
              
              <ag-form-group>
                <label for="bio">Bio (max 500 characters)</label>
                <textarea id="bio" name="bio" maxlength="500" placeholder="Tell visitors about yourself...">${safeBio}</textarea>
                <div class="char-count">
                  <span id="bioCharCount">${(c.bio || '').length}</span>/500
                </div>
              </ag-form-group>
            </div>
            
            <div class="section">
              <div class="section-title">Branding</div>
              
              <ag-form-group>
                <label>${this.isCompanyAccount() ? 'Logo' : 'Profile Photo'}</label>
                <ag-link-upload image-type="profile">
                  <input
                    slot="url-input"
                    type="url"
                    class="url-input"
                    name="logoUrl"
                    value="${rawLogoUrl}"
                    placeholder="https://example.com/logo.png">
                  <input
                    slot="file-input"
                    type="file"
                    class="file-input"
                    accept="image/*">
                  <img
                    slot="preview"
                    src="${escapeAttribute(safeLogoUrl)}"
                    class="image-preview"
                    alt="Logo preview"
                    style="${safeLogoUrl ? 'display: block;' : 'display: none;'}">
                </ag-link-upload>
                <button type="button" class="btn btn-sm btn-danger" id="removeLogoBtn" style="${rawLogoUrl ? '' : 'display: none;'}">Remove image</button>
              </ag-form-group>

              <ag-form-group>
                <label>Image Shape</label>
                <div class="shape-selector">
                  <label class="shape-option ${selectedShape === 'circle' ? 'active' : ''}">
                    <input type="radio" name="logoShape" value="circle" ${selectedShape === 'circle' ? 'checked' : ''}>
                    <span>Circle</span>
                  </label>
                  <label class="shape-option ${selectedShape === 'square' ? 'active' : ''}">
                    <input type="radio" name="logoShape" value="square" ${selectedShape === 'square' ? 'checked' : ''}>
                    <span>Square</span>
                  </label>
                  <label class="shape-option ${selectedShape === 'rectangle' ? 'active' : ''}">
                    <input type="radio" name="logoShape" value="rectangle" ${selectedShape === 'rectangle' ? 'checked' : ''}>
                    <span>Rectangle (wide)</span>
                  </label>
                </div>
                <small id="logoShapeHint" class="shape-hint"></small>
              </ag-form-group>

              <ag-form-group>
                <label>Logo Border</label>
                <label class="toggle-option">
                  <input type="checkbox" name="logoBorderEnabled" id="logoBorderEnabled" ${(c.logoBorderEnabled ?? true) ? 'checked' : ''}>
                  Show border around logo
                </label>
                <div id="borderControls" style="${(c.logoBorderEnabled ?? true) ? '' : 'display: none;'}">
                  <div class="border-controls">
                    <div class="border-control-item">
                      <label for="logoBorderWidth">Thickness: <span id="borderWidthValue">${c.logoBorderWidth ?? 4}</span>px</label>
                      <input type="range" id="logoBorderWidth" name="logoBorderWidth" min="1" max="8" value="${c.logoBorderWidth ?? 4}">
                    </div>
                    <div class="border-control-item">
                      <label for="logoBorderColor">Colour</label>
                      <input type="color" class="color-control" id="logoBorderColor" name="logoBorderColor" value="${escapeAttribute(c.logoBorderColor || '#ffffff')}">
                    </div>
                  </div>
                  <small class="shape-hint">Tip: Choose a colour that contrasts with your page background.</small>
                </div>
              </ag-form-group>

            </div>

            <div class="section">
              <div class="section-title">Background</div>

              <ag-form-group>
                <label>Background Image (optional)</label>
                <ag-link-upload image-type="background">
                  <input
                    slot="url-input"
                    type="url"
                    class="url-input"
                    name="backgroundUrl"
                    value="${rawBackgroundUrl}"
                    placeholder="https://example.com/background.jpg">
                  <input
                    slot="file-input"
                    type="file"
                    class="file-input"
                    accept="image/*">
                  <img
                    slot="preview"
                    src="${escapeAttribute(safeBackgroundUrl)}"
                    class="image-preview"
                    alt="Background preview"
                    style="${safeBackgroundUrl ? 'display: block;' : 'display: none;'}">
                </ag-link-upload>
                <button type="button" class="btn btn-sm btn-danger" id="removeBgBtn" style="${rawBackgroundUrl ? '' : 'display: none;'}">Remove image</button>
              </ag-form-group>

              <ag-form-group>
                <label for="backgroundBlur">Background Blur: <span id="bgBlurValue">${c.backgroundBlur ?? 0}</span>px</label>
                <input type="range" id="backgroundBlur" name="backgroundBlur" min="0" max="20" value="${c.backgroundBlur ?? 0}" class="blur-range">
              </ag-form-group>
            </div>

            <div class="section">
              <div class="section-title">Colors</div>
              
              <div class="color-group">
                <div class="color-item">
                  <input class="color-control" type="color" id="accentColor" name="accentColor" value="${safeAccentColor}">
                  <label for="accentColor">Accent</label>
                </div>
                <div class="color-item">
                  <input class="color-control" type="color" id="backgroundColor" name="backgroundColor" value="${safeBgColor}">
                  <label for="backgroundColor">Background</label>
                </div>
              </div>
            </div>
            
            <div class="section">
              <div class="section-title">Featured Content</div>
              <p class="section-description">Add a highlighted or featured content block that can be set to appear above or below your links.</p>
              
              <ag-form-group>
                <label for="featuredTitle">Title *</label>
                <input class="form-control" type="text" id="featuredTitle" name="featuredTitle" 
                  value="${escapeAttribute(this.featuredBlock?.title || '')}" 
                  placeholder="Featured content title" 
                  maxlength="100">
              </ag-form-group>
              
              <ag-form-group>
                <label for="featuredDescription">Description * (max 250 characters)</label>
                <textarea id="featuredDescription" name="featuredDescription" 
                  placeholder="Describe your featured content..." 
                  maxlength="250" 
                  rows="3">${escapeHtml(this.featuredBlock?.description || '')}</textarea>
                <div class="char-count">
                  <span id="charCount">${this.featuredBlock?.description?.length || 0}</span>/250
                </div>
              </ag-form-group>
              
              <ag-form-group>
                <label>Image (optional)</label>
                <ag-link-upload image-type="featured">
                  <input 
                    slot="url-input"
                    type="url" 
                    class="url-input" 
                    name="featuredImage"
                    value="${rawFeaturedImageUrl}"
                    placeholder="https://example.com/featured.jpg">
                  <input 
                    slot="file-input"
                    type="file" 
                    class="file-input" 
                    accept="image/*">
                  <img 
                    slot="preview"
                    src="${escapeAttribute(safeFeaturedImageUrl)}" 
                    class="image-preview" 
                    alt="Featured image preview"
                    style="${safeFeaturedImageUrl ? 'display: block;' : 'display: none;'}">
                </ag-link-upload>
              </ag-form-group>
              
              <ag-form-group>
                <label for="featuredPosition">Position</label>
                <select id="featuredPosition" name="featuredPosition">
                  <option value="above" ${this.featuredBlock?.position === 'above' ? 'selected' : ''}>Above links</option>
                  <option value="below" ${this.featuredBlock?.position === 'below' ? 'selected' : ''}>Below links</option>
                </select>
              </ag-form-group>

              <ag-form-group>
                <label for="featuredLinkUrl">Link URL (optional)</label>
                <input class="form-control" type="url" id="featuredLinkUrl" name="featuredLinkUrl"
                  value="${escapeAttribute(this.featuredBlock?.linkUrl || '')}"
                  placeholder="https://example.com">
                <small class="shape-hint">If set, the entire featured block becomes clickable.</small>
              </ag-form-group>

              <ag-form-group>
                <label class="toggle-option">
                  <input type="checkbox" name="featuredVisible" id="featuredVisible" ${(this.featuredBlock?.visible ?? true) ? 'checked' : ''}>
                  Show featured block on page
                </label>
              </ag-form-group>

              ${this.featuredBlock ? `
                <div class="ag-button-group right">
                  <button type="button" class="btn btn-danger" id="removeFeatured">Remove Featured Block</button>
                </div>
              ` : ''}
            </div>

            <div class="section">
              <div class="section-title">Disclaimer</div>
              <p class="section-description">Add an optional collapsible disclaimer or legal notice to the bottom of your page.</p>

              <ag-form-group>
                <label class="toggle-option">
                  <input type="checkbox" name="disclaimerVisible" id="disclaimerVisible" ${(c.disclaimerVisible ?? true) ? 'checked' : ''}>
                  Show disclaimer on page
                </label>
              </ag-form-group>

              <ag-form-group>
                <label for="disclaimerTitle">Toggle Link Text</label>
                <input class="form-control" type="text" id="disclaimerTitle" name="disclaimerTitle"
                  value="${escapeAttribute(c.disclaimerTitle || '')}"
                  placeholder="Disclaimer"
                  maxlength="200">
              </ag-form-group>

              <ag-form-group>
                <label for="disclaimer">Disclaimer Content (max ${CHAR_LIMIT} characters)</label>
                <textarea id="disclaimer" name="disclaimer"
                  placeholder="Enter your disclaimer or legal notice here..."
                  rows="6">${escapeHtml(c.disclaimer || '')}</textarea>
                <div class="char-count">
                  <span id="disclaimerCharCount">${c.disclaimer?.length || 0}</span>/${CHAR_LIMIT}
                </div>
                <button type="button" class="btn btn-sm btn-danger" id="clearDisclaimerBtn" style="${c.disclaimer?.trim() ? '' : 'display: none;'}">Clear disclaimer</button>
              </ag-form-group>
            </div>

            <div class="ag-button-group right">
              <button type="submit" class="btn btn-primary" ${this.saving ? 'disabled' : ''}>
                ${this.saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </ag-panel-content>
      </ag-panel>
    `;
  }

  protected afterRender(): void {
    const form = this.$('#pageForm') as HTMLFormElement;
    form?.addEventListener('submit', (e) => this.handleSave(e));

    const bioField = this.$('#bio') as HTMLTextAreaElement;
    const bioCharCount = this.$('#bioCharCount');
    if (bioField && bioCharCount) {
      bioField.addEventListener('input', () => {
        bioCharCount.textContent = String(bioField.value.length);
      });
    }

    const descriptionField = this.$('#featuredDescription') as HTMLTextAreaElement;
    const charCount = this.$('#charCount');
    if (descriptionField && charCount) {
      descriptionField.addEventListener('input', () => {
        charCount.textContent = String(descriptionField.value.length);
      });
    }

    const removeBtn = this.$('#removeFeatured');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => this.handleRemoveFeatured());
    }

    const disclaimerField = this.$('#disclaimer') as HTMLTextAreaElement;
    const disclaimerCharCount = this.$('#disclaimerCharCount');
    const clearDisclaimerBtn = this.$('#clearDisclaimerBtn') as HTMLButtonElement;
    if (disclaimerField && disclaimerCharCount) {
      disclaimerField.addEventListener('input', () => {
        disclaimerCharCount.textContent = String(disclaimerField.value.length);
        if (clearDisclaimerBtn) {
          clearDisclaimerBtn.style.display = disclaimerField.value.trim() ? '' : 'none';
        }
      });
    }
    if (clearDisclaimerBtn && disclaimerField && disclaimerCharCount) {
      clearDisclaimerBtn.addEventListener('click', () => {
        disclaimerField.value = '';
        disclaimerCharCount.textContent = '0';
        clearDisclaimerBtn.style.display = 'none';
      });
    }

    const logoUrlInput = this.$('input[name="logoUrl"]') as HTMLInputElement;
    const removeLogoBtn = this.$('#removeLogoBtn') as HTMLButtonElement;
    if (logoUrlInput && removeLogoBtn) {
      logoUrlInput.addEventListener('input', () => {
        removeLogoBtn.style.display = logoUrlInput.value ? '' : 'none';
        this.updateLogoShapeHint();
      });
      removeLogoBtn.addEventListener('click', () => {
        logoUrlInput.value = '';
        logoUrlInput.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }

    const bgUrlInput = this.$('input[name="backgroundUrl"]') as HTMLInputElement;
    const removeBgBtn = this.$('#removeBgBtn') as HTMLButtonElement;
    if (bgUrlInput && removeBgBtn) {
      bgUrlInput.addEventListener('input', () => {
        removeBgBtn.style.display = bgUrlInput.value ? '' : 'none';
      });
      removeBgBtn.addEventListener('click', () => {
        bgUrlInput.value = '';
        bgUrlInput.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }

    this.updateLogoShapeHint();

    const borderToggle = this.$('#logoBorderEnabled') as HTMLInputElement;
    const borderControls = this.$('#borderControls') as HTMLElement;
    if (borderToggle && borderControls) {
      borderToggle.addEventListener('change', () => {
        borderControls.style.display = borderToggle.checked ? '' : 'none';
      });
    }

    const borderWidthInput = this.$('#logoBorderWidth') as HTMLInputElement;
    const borderWidthValue = this.$('#borderWidthValue');
    if (borderWidthInput && borderWidthValue) {
      borderWidthInput.addEventListener('input', () => {
        borderWidthValue.textContent = borderWidthInput.value;
      });
    }

    const bgBlurInput = this.$('#backgroundBlur') as HTMLInputElement;
    const bgBlurValue = this.$('#bgBlurValue');
    if (bgBlurInput && bgBlurValue) {
      bgBlurInput.addEventListener('input', () => {
        bgBlurValue.textContent = bgBlurInput.value;
      });
    }
  }

  private isCompanyAccount(): boolean {
    const user = session.getUser();
    return user?.accountType === 'company';
  }

  private async loadConfig(): Promise<void> {
    this.loading = true;
    const result = await api.getPageConfig();
    if (result.success && result.data) {
      this.config = result.data;
      if (!this.config.blocks || !Array.isArray(this.config.blocks)) {
        this.config.blocks = [];
      }

      const featuredBlockData = this.config.blocks?.find(b => b.type === 'featured');
      if (featuredBlockData && featuredBlockData.content) {
        try {
          this.featuredBlock = JSON.parse(featuredBlockData.content);
        } catch {
          this.featuredBlock = null;
        }
      }
    }
    this.loading = false;
    this.render();
    this.afterRender();
  }

  private async handleSave(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const featuredTitle = stripHtml((formData.get('featuredTitle') as string || '').trim());
    const featuredDescription = stripHtml((formData.get('featuredDescription') as string || '').trim());
    const featuredImage = (formData.get('featuredImage') as string || '').trim();
    const featuredPosition = (formData.get('featuredPosition') as string || 'above') as 'above' | 'below';
    const featuredLinkUrl = (formData.get('featuredLinkUrl') as string || '').trim();
    const featuredVisible = formData.get('featuredVisible') === 'on';

    let featuredBlockData: FeaturedBlockData | null = null;
    if (featuredTitle || featuredDescription) {
      if (!featuredTitle) {
        toast.error('Featured block title is required');
        return;
      }
      if (!featuredDescription) {
        toast.error('Featured block description is required');
        return;
      }
      if (featuredDescription.length > 250) {
        toast.error('Featured block description must be 250 characters or less');
        return;
      }

      featuredBlockData = {
        title: featuredTitle.slice(0, 100),
        description: featuredDescription.slice(0, 250),
        imageUrl: featuredImage || undefined,
        position: featuredPosition,
        linkUrl: featuredLinkUrl || undefined,
        visible: featuredVisible,
      };
    }

    const disclaimerTitle = stripHtml((formData.get('disclaimerTitle') as string || '').trim());
    const disclaimer = stripHtml((formData.get('disclaimer') as string || '').trim());

    if (disclaimer.length > CHAR_LIMIT) {
      toast.error(`Disclaimer must be ${CHAR_LIMIT} characters or less`);
      return;
    }

    const rawLogoUrlVal = formData.get('logoUrl') as string;
    const rawBgUrlVal = formData.get('backgroundUrl') as string;

    const data = {
      title: stripHtml(formData.get('title') as string),
      bio: stripHtml(formData.get('bio') as string),
      logoUrl: rawLogoUrlVal === '' ? null : (rawLogoUrlVal || undefined),
      backgroundUrl: rawBgUrlVal === '' ? null : (rawBgUrlVal || undefined),
      logoShape: (formData.get('logoShape') as string || 'circle') as LogoShape,
      logoBorderEnabled: formData.get('logoBorderEnabled') === 'on',
      logoBorderWidth: parseInt(formData.get('logoBorderWidth') as string) || 4,
      logoBorderColor: formData.get('logoBorderColor') as string || '#ffffff',
      backgroundBlur: parseInt(formData.get('backgroundBlur') as string) || 0,
      accentColor: formData.get('accentColor') as string,
      backgroundColor: formData.get('backgroundColor') as string,
      blocks: this.config?.blocks || [],
      disclaimerTitle: disclaimerTitle,
      disclaimer: disclaimer,
      disclaimerVisible: formData.get('disclaimerVisible') === 'on',
    };

    if (!Array.isArray(data.blocks)) {
      data.blocks = [];
    }

    if (featuredBlockData) {
      const existingFeaturedIndex = data.blocks.findIndex(b => b.type === 'featured');
      const blockContent = JSON.stringify(featuredBlockData);

      if (existingFeaturedIndex >= 0) {
        data.blocks[existingFeaturedIndex] = {
          ...data.blocks[existingFeaturedIndex],
          content: blockContent,
        };
      } else {
        data.blocks.push({
          id: crypto.randomUUID(),
          type: 'featured',
          content: blockContent,
          order: featuredPosition === 'above' ? 0 : 9999,
        });
      }
    } else {
      data.blocks = data.blocks.filter(b => b.type !== 'featured');
    }

    this.saving = true;
    this.render();
    this.afterRender();

    const result = await api.updatePageConfig(data);
    if (result.success && result.data) {
      this.config = result.data;
      this.featuredBlock = featuredBlockData;
      toast.success('Page settings saved successfully!');
    } else {
      toast.error('Failed to save page settings. Please try again.');
    }

    this.saving = false;
    this.render();
    this.afterRender();
  }

  private updateLogoShapeHint(): void {
    const hint = this.$('#logoShapeHint');
    if (!hint) return;

    const logoUrlInput = this.$('input[name="logoUrl"]') as HTMLInputElement;
    const urlValue = logoUrlInput?.value || '';
    if (!urlValue) {
      hint.textContent = '';
      return;
    }

    const resolvedUrl = resolveImageUrl(urlValue);
    if (!resolvedUrl) {
      hint.textContent = '';
      return;
    }

    const img = new Image();
    img.onload = () => {
      const ratio = img.naturalWidth / img.naturalHeight;
      if (ratio > 1.5) {
        hint.textContent = 'Tip: Rectangle works best for this wide image.';
      } else if (ratio >= 0.8 && ratio <= 1.25) {
        hint.textContent = 'Tip: Circle or Square works best for this image.';
      } else {
        hint.textContent = 'Tip: Square is a good choice for this image.';
      }
    };
    img.onerror = () => { hint.textContent = ''; };
    img.src = resolvedUrl;
  }

  private async handleRemoveFeatured(): Promise<void> {
    if (!confirm('Remove featured content block?')) return;

    this.featuredBlock = null;

    const data = {
      blocks: (this.config?.blocks || []).filter(b => b.type !== 'featured'),
    };

    this.saving = true;
    this.render();
    this.afterRender();

    const result = await api.updatePageConfig(data);
    if (result.success && result.data) {
      this.config = result.data;
      toast.success('Featured block removed successfully!');
    } else {
      toast.error('Failed to remove featured block. Please try again.');
    }

    this.saving = false;
    this.render();
    this.afterRender();
  }
}

customElements.define('page-editor', PageEditor);
